import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { OrderDashboard, ConditionReport, getErrorMessage } from '../types';
import api from '../api';
import { useTranslation } from '../i18n';
import { formatDate, formatDateTime } from '../formatDate';
import { StarRating } from '../components/StarRating';
import { useApiRequest } from '../useApiRequest';
import { Tooltip } from '../components/Tooltip';
import { ContactDetails } from '../components/ContactDetails';
import { OwnerContactPreview } from '../components/OwnerContactPreview';
import { ConditionReportPanel } from '../components/ConditionReportPanel';
import { FormLabel } from '../components/FormLabel';
import { FormLegend } from '../components/FormLegend';
import { FieldError } from '../components/FieldError';
import { validateFormSubmit } from '../utils/formValidation';
import { useOrderHub } from '../useOrderHub';
import { canRenterCancelConfirmed, isOwnerPickupHandoverComplete } from '../orderCancel';

interface InvoiceSummary {
  id: number;
  number: string;
  kind: number;
  amount: number;
  currency: string;
  issueDate: string;
  isCreditNote: boolean;
  correctsNumber?: string;
}

export const OrderDashboardPage: React.FC = () => {
  const { t, lang } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderDashboard | null>(null);
  const [handoverMessage, setHandoverMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [acceptedContract, setAcceptedContract] = useState(false);
  const [contractError, setContractError] = useState('');
  const [showContract, setShowContract] = useState(false);
  const [contractHtml, setContractHtml] = useState('');
  const [contractLoading, setContractLoading] = useState(false);
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [reports, setReports] = useState<ConditionReport[]>([]);
  const [cancelling, setCancelling] = useState(false);
  const { loading: ratingSubmitting, call: rateRequest } = useApiRequest(async (newRating: number) => {
    await api.post(`/orders/${id}/rating`, { rating: newRating });
    setOrder(prev => {
      if (!prev) return prev;
      return prev.role === 'lender'
        ? { ...prev, renterRating: newRating }
        : { ...prev, lenderRating: newRating };
    });
    setSuccess(t('dashboard.rating_success'));
  });

  const fetchOrder = useCallback(async () => {
    try {
      const res = await api.get<OrderDashboard>(`/orders/${id}/dashboard`);
      setOrder(res.data);
      if (res.data.handoverMessage) {
        setHandoverMessage(res.data.handoverMessage);
      }
    } catch (err) {
      setError(getErrorMessage(err, t('dashboard.load_error')));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const fetchReports = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get<ConditionReport[]>(`/orders/${id}/reports`);
      setReports(res.data);
    } catch {
      setReports([]);
    }
  }, [id]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // Refresh order + reports in realtime when the other party acts at the meeting.
  useOrderHub(id, useCallback(() => {
    void fetchOrder();
    void fetchReports();
  }, [fetchOrder, fetchReports]));

  const fetchInvoices = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get<InvoiceSummary[]>(`/orders/${id}/invoices`);
      setInvoices(res.data);
    } catch {
      setInvoices([]);
    }
  }, [id]);

  useEffect(() => {
    if (order && (order.status === 1 || order.status === 3)) {
      void fetchInvoices();
    }
  }, [order, fetchInvoices]);

  const downloadInvoice = async (invoiceId: number, fileName: string) => {
    try {
      const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(getErrorMessage(err, t('invoice.download_error')));
    }
  };

  const fetchContract = useCallback(async () => {
    if (!id || contractHtml) return;
    setContractLoading(true);
    try {
      const res = await api.get<{ html: string }>(`/contracts/${id}`);
      setContractHtml(res.data.html);
    } catch {
      // Contract may not be available yet for renter in pending state
    } finally {
      setContractLoading(false);
    }
  }, [id, contractHtml]);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setContractError('');
    if (!validateFormSubmit(e)) return;
    if (!acceptedContract) {
      setContractError(t('contract.required'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post(`/orders/${id}/respond`, { handoverMessage, acceptedContract: true });
      setSuccess(t('rental_response.success'));
      await fetchOrder();
    } catch (err) {
      setError(getErrorMessage(err, t('rental_response.submit_error')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRate = async (newRating: number) => {
    if (!order) return;
    setError('');
    try {
      await rateRequest(newRating);
    } catch (err) {
      setError(getErrorMessage(err, t('dashboard.rating_error')));
    }
  };

  const handleCancel = async (decline: boolean) => {
    const confirmKey = decline ? 'dashboard.decline_confirm' : 'dashboard.cancel_confirm';
    if (!window.confirm(t(confirmKey))) return;
    setCancelling(true);
    setError('');
    try {
      await api.post(`/orders/${id}/cancel`);
      setSuccess(decline ? t('dashboard.decline_success') : t('dashboard.cancel_success'));
      await fetchOrder();
    } catch (err) {
      setError(getErrorMessage(err, t('dashboard.cancel_error')));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <p className="text-muted">{t('rental_response.loading')}</p>;

  if (error && !order) {
    return (
      <div className="alert alert-error">
        {error}
        <button className="dismiss" onClick={() => navigate('/orders')}>&times;</button>
      </div>
    );
  }

  if (!order) return null;

  const isPending = order.status === 0;
  const isConfirmed = order.status === 1;
  const isReturned = order.status === 3;
  const isCancelled = order.status === 4;
  const deadline = new Date(order.rentalResponseDeadline);
  const isExpired = order.status === 2 || (isPending && deadline < new Date());
  const isLender = order.role === 'lender';
  const isRenter = order.role === 'renter';
  const canCancelConfirmed = isConfirmed && (
    isRenter
      ? canRenterCancelConfirmed(order.rentalStartDate)
      : isLender
        ? !isOwnerPickupHandoverComplete(reports)
        : false
  );

  return (
    <div>
      <Link to="/orders" className="text-muted" style={{ textDecoration: 'none', fontSize: '0.85rem' }}>
        &larr; {t('dashboard.back_to_orders')}
      </Link>

      <h2 className="section-title" style={{ marginTop: '0.75rem' }}>
        {t('orders.order_id', { id: order.id })}
      </h2>

      {success && (
        <div className="alert alert-success">
          {success}
          <button className="dismiss" onClick={() => setSuccess('')}>&times;</button>
        </div>
      )}
      {error && (
        <div className="alert alert-error">
          {error}
          <button className="dismiss" onClick={() => setError('')}>&times;</button>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <p className="order-meta" style={{ marginBottom: '0.25rem' }}>
              <span className={`order-status order-status-${order.status}`}>
                {t(`orders.status.${statusLabel(order.status)}`)}
              </span>
              &nbsp;&nbsp;
              <span className="dashboard-role-badge" style={{
                display: 'inline-block',
                padding: '0.15rem 0.55rem',
                borderRadius: '4px',
                fontSize: '0.78rem',
                fontWeight: 600,
                background: isLender ? '#e3f2fd' : '#f3e5f5',
                color: isLender ? '#1565c0' : '#7b1fa2',
              }}>
                {t(`dashboard.role.${order.role}`)}
              </span>
            </p>
            <p className="order-meta">
              {t('orders.placed')} {formatDate(order.orderDate, lang)}
            </p>
            <p className="order-meta">
              {t('orders.rental')} {formatDate(order.rentalStartDate, lang)} &ndash; {formatDate(order.rentalEndDate, lang)}
            </p>
            {isPending && !isExpired && (
              <p className="order-meta">
                {t('rental_response.deadline')}: {formatDateTime(deadline, lang)}
              </p>
            )}
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.2rem' }}>
              {order.items.map(i => (
                <li key={i.id} style={{ fontSize: '0.9rem' }}>{i.product?.name} &times; {i.quantity}</li>
              ))}
            </ul>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="order-total">{t('orders.total', { amount: order.totalAmount })}</div>
          </div>
        </div>
      </div>

      {/* Status-specific content */}
      {isExpired && (
        <div className="card" style={{ borderLeft: '3px solid #c62828' }}>
          <p style={{ fontWeight: 600, color: 'var(--color-error, #c0392b)' }}>
            {isRenter ? t('dashboard.renter_expired') : t('rental_response.expired')}
          </p>
        </div>
      )}

      {isCancelled && (
        <div className="card" style={{ borderLeft: '3px solid #c62828' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: 'var(--color-error, #c0392b)' }}>
            {t('dashboard.cancelled_title')}
          </h3>
          <p style={{ fontSize: '0.9rem' }}>
            {isRenter ? t('dashboard.cancelled_renter') : t('dashboard.cancelled_lender')}
          </p>
        </div>
      )}

      {isConfirmed && (
        <div className="card" style={{ borderLeft: '3px solid #27ae60' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: '#27ae60' }}>
            {t('dashboard.confirmed_title')}
          </h3>
          {order.handoverMessage && (
            <div style={{ marginBottom: '0.75rem' }}>
              <p className="order-meta" style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{t('rental_response.message_label')}</p>
              <p style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{order.handoverMessage}</p>
            </div>
          )}
          {canCancelConfirmed && (
            <div style={{ marginTop: '1rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => handleCancel(false)}
                disabled={cancelling}
              >
                {cancelling && <span className="btn-spinner" aria-hidden="true" />}
                {t('dashboard.cancel_button')}
              </button>
            </div>
          )}
        </div>
      )}

      {(isConfirmed || isReturned) && (
        <ConditionReportPanel
          orderId={order.id}
          role={order.role}
          editable={isConfirmed}
          canConfirm={isConfirmed || isReturned}
          reports={reports}
          lenderRating={order.lenderRating}
          renterRating={order.renterRating}
          lang={lang}
          onChanged={() => { void fetchOrder(); void fetchReports(); }}
          onError={setError}
          onSuccess={setSuccess}
        />
      )}

      {(isConfirmed || isReturned) && (order.ownerContact || order.customerContact) && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          {isRenter && order.ownerContact && (
            <ContactDetails
              title={t('dashboard.owner_contact')}
              contact={order.ownerContact}
              showAddresses
            />
          )}
          {isLender && order.customerContact && (
            <ContactDetails
              title={t('dashboard.customer_contact')}
              contact={order.customerContact}
              showAddresses={false}
            />
          )}
        </div>
      )}

      {(isConfirmed || isReturned) && (
        <div className="card" style={{ marginTop: '0.5rem' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={async () => {
              try {
                const res = await api.get(`/contracts/${order.id}/pdf`, { responseType: 'blob' });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = `smlouva-RS-${String(order.id).padStart(6, '0')}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (err) {
                setError(getErrorMessage(err, t('contract.load_error')));
              }
            }}
          >
            {t('contract.download_pdf')}
          </button>
          {invoices.length > 0 && (
            <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {invoices.map(inv => (
                <button
                  key={inv.id}
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => downloadInvoice(inv.id, inv.number)}
                >
                  {inv.isCreditNote
                    ? t('invoice.download_credit', { number: inv.number })
                    : t(`invoice.download.${inv.kind === 0 ? 'commission' : 'rental'}`, { number: inv.number })}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {isReturned && (
        <div className="card" style={{ borderLeft: '3px solid #1565c0' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: '#1565c0' }}>
            {t('dashboard.returned_title')}
          </h3>
          <p className="order-meta">
            {t('dashboard.returned_fee', { amount: order.totalAmount })}
          </p>
          {(() => {
            const givenRating = isLender ? order.renterRating : order.lenderRating;
            const receivedRating = isLender ? order.lenderRating : order.renterRating;
            return (
              <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  <p className="order-meta" style={{ marginBottom: '0.25rem' }}>
                    {isLender ? t('reports.rate_customer') : t('reports.rate_owner')}
                  </p>
                  {givenRating != null ? (
                    <StarRating value={givenRating} />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <StarRating
                        value={ratingSubmitting ? 0 : 5}
                        onChange={ratingSubmitting ? undefined : handleRate}
                      />
                      {ratingSubmitting && (
                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                          {t('dashboard.rating_submitting')}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <p className="order-meta" style={{ marginBottom: '0.25rem' }}>
                    {t('dashboard.rating_received_label')}
                  </p>
                  {receivedRating != null ? (
                    <StarRating value={receivedRating} />
                  ) : (
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                      {t('dashboard.rating_not_received')}
                    </span>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {isPending && !isExpired && isRenter && (
        <div className="card" style={{ borderLeft: '3px solid #856404' }}>
          <p style={{ fontWeight: 600, color: '#856404' }}>
            {t('dashboard.renter_pending')}
          </p>
          {order.renterNotes && (
            <div style={{ marginTop: '0.75rem' }}>
              <p className="order-meta" style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                {t('rental.notes_label')}
              </p>
              <p style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{order.renterNotes}</p>
            </div>
          )}
          <div style={{ marginTop: '1rem' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => handleCancel(false)}
              disabled={cancelling}
            >
              {cancelling && <span className="btn-spinner" aria-hidden="true" />}
              {t('dashboard.cancel_request_button')}
            </button>
          </div>
        </div>
      )}

      {isPending && !isExpired && isLender && (
        <div className="card" style={{ borderLeft: '3px solid #856404' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
            {t('rental_response.title')}
          </h3>
          <p className="order-meta" style={{ marginBottom: '0.5rem' }}>
            {t('rental_response.customer')}: {order.customerName}
          </p>
          {order.renterNotes && (
            <div style={{ marginBottom: '0.75rem' }}>
              <p className="order-meta" style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                {t('rental_response.renter_notes_label')}
              </p>
              <p style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{order.renterNotes}</p>
            </div>
          )}
          <form onSubmit={handleConfirm}>
            <FormLegend />
            <div className="form-group">
              <FormLabel required htmlFor="handoverMessage">
                {t('rental_response.message_label')}{' '}
                <Tooltip content={t('rental_response.message_help')} />
              </FormLabel>
              <textarea
                id="handoverMessage"
                className="form-input"
                rows={5}
                value={handoverMessage}
                onChange={e => setHandoverMessage(e.target.value)}
                placeholder={t('rental_response.message_placeholder')}
                required
                aria-required="true"
              />
            </div>
            {order.ownerContact && (
              <OwnerContactPreview contact={order.ownerContact} />
            )}
            <div style={{ marginTop: '0.75rem', marginBottom: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => { setShowContract(v => !v); if (!contractHtml) fetchContract(); }}
                style={{ marginBottom: '0.5rem' }}
              >
                {showContract ? t('contract.hide') : t('contract.show')}
              </button>
              {showContract && contractLoading && (
                <p className="text-muted" style={{ fontSize: '0.85rem' }}>{t('contract.loading')}</p>
              )}
              {showContract && contractHtml && (
                <div
                  style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid var(--color-border, #ddd)', borderRadius: '6px', padding: '0.75rem', fontSize: '0.82rem', marginBottom: '0.5rem', background: 'var(--color-surface, #fafafa)' }}
                  dangerouslySetInnerHTML={{ __html: contractHtml }}
                />
              )}
              <div className={`form-group${contractError ? ' form-group--error' : ''}`} style={{ marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={acceptedContract}
                  onChange={e => { setAcceptedContract(e.target.checked); setContractError(''); }}
                  disabled={submitting}
                  aria-invalid={contractError ? true : undefined}
                  aria-describedby={contractError ? 'contract-error' : undefined}
                />
                {t('contract.accept_label')}
              </label>
              <FieldError id="contract-error" message={contractError} />
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t('rental_response.submitting') : t('rental_response.submit')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginLeft: '0.5rem' }}
              onClick={() => handleCancel(true)}
              disabled={cancelling || submitting}
            >
              {cancelling && <span className="btn-spinner" aria-hidden="true" />}
              {t('dashboard.decline_button')}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

const STATUS_LABELS: Record<number, string> = {
  0: 'PendingApproval',
  1: 'Confirmed',
  2: 'Expired',
  3: 'Returned',
  4: 'Cancelled',
};

function statusLabel(status: number): string {
  return STATUS_LABELS[status] ?? 'PendingApproval';
}
